import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EBannerAction, EDealsDocument } from '@core-constants/deal.const';
import { ICarousel, IDealItem, IPackageDealsResponse, IPackageDeal, IPackageDealTranslation } from '@core-models/deals.model';
import { ILanguageComponent } from '@core-models/generic.model';
import { TranslateService } from '@shared-services/translate.service';
import { PackageDealsDataService } from '@core-data-services/package-deals.data-service';

@Component({
  selector: 'app-package-deals',
  templateUrl: './package-deals.component.html',
  styleUrls: ['./package-deals.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageDealsComponent implements OnInit, ILanguageComponent
{
  public flashDeal: IDealItem | undefined = undefined;

  public backgroundColor: string = "";
  public bannerImageDesktop: string = "";
  public bannerImageMobile: string = "";
  public bannerAction: number = EBannerAction.None;
  public showDomainSearch: boolean = false;

  public carouselItems: ICarousel[];
  public dealDetail: IPackageDeal;
  public detalItems: IDealItem[];
  public dealTranslation: IPackageDealTranslation;

  constructor(@Inject(PLATFORM_ID) private platformId,
    protected translateService: TranslateService,
    protected packageDealsDataService: PackageDealsDataService,
    private ref: ChangeDetectorRef,
    private renderer: Renderer2,
    private destroyRef$: DestroyRef
  ) { }

  public get isDealClickable(): boolean
  {
    return this.bannerAction !== EBannerAction.None;
  }

  public get termsConditionsPDF(): string
  {
    return EDealsDocument.TermsConditions;
  }

  public ngOnInit(): void
  {
    this.registerEventListeners();
    this.getBundle();
  }

  public registerEventListeners(): void
  {
    this.translateService.onTranslateChange()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
        next: () =>
        {
          this.getBundle();
        }
      });
  }

  private getBundle(): void
  {
    this.packageDealsDataService.getBundle()
      .pipe(takeUntilDestroyed(this.destroyRef$))
      .subscribe({
      next: (response: IPackageDealsResponse) =>
      {
        this.setDealInfo(response);
        this.ref.detectChanges();
      }
    });
  }

  private setDealInfo(data: IPackageDealsResponse): void
  {
    if (data)
    {
      this.carouselItems = data.carouselItems;

      this.detalItems = data.items;
      this.flashDeal = this.detalItems?.find((item: IDealItem) => item.flash);

      if (data.packageDeal)
      {
        this.backgroundColor = data.packageDeal.backgroundColor;
        this.dealDetail = data.packageDeal;
        this.dealTranslation = data.packageDeal.translations[0];
        this.bannerAction = data.packageDeal.bannerAction;
        this.showDomainSearch = data.packageDeal.showDomainSearch;

        this.bannerImageDesktop = this.dealTranslation?.banner.id;
        this.bannerImageMobile = this.dealTranslation?.bannerMovil.id;
      }
    }
  }

  public onDealClicked(): void
  {
    switch (this.bannerAction)
    {
      case EBannerAction.None:
        return;
      case EBannerAction.Scroll:
        this.scrollToElement(this.dealDetail.scrollItem);
        break;
      case EBannerAction.OpenUrl:
        this.openUrlInNewTab(this.dealDetail.url);
        break;
    }
  }

  private scrollToElement(selector: string): void
  {
    if (isPlatformBrowser(this.platformId))
    {
      const element = this.renderer.selectRootElement(selector, true);
      if (element)
      {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  private openUrlInNewTab(url: string): void
  {
    if (isPlatformBrowser(this.platformId) && url)
    {
      window.open(url, '_blank');
    }
  }
}
